import { useContext, useEffect, useState } from "react";
import { UserDataContext } from "../context/User";
import axios from "axios";
import { Upload, CheckCircle } from "lucide-react";

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const ALLOWED_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp"];

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isAllowedImage = (file) => {
  const extension = `.${(file?.name || "").split(".").pop()?.toLowerCase()}`;
  return ALLOWED_IMAGE_TYPES.includes(file?.type) && ALLOWED_IMAGE_EXTENSIONS.includes(extension);
};

const QuestForm = ({ onSuccess, onError, challenges = [], initialChallengeId = "" }) => {
  const { userData } = useContext(UserDataContext);
  const [title, setTitle]           = useState("");
  const [description, setDescription] = useState("");
  const [member_bio, setMember_bio] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [file, setFile]             = useState(null);
  const [preview, setPreview]       = useState(null);
  const [loading, setLoading]       = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const selectedChallenge = challenges.find((item) => item._id === challengeId);
  const selectedFee = selectedChallenge?.entryFeeCents || 0;

  useEffect(() => {
    if (initialChallengeId && challenges.some((item) => item._id === initialChallengeId)) {
      setChallengeId(initialChallengeId);
    }
  }, [initialChallengeId, challenges]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const f = e.target.files[0];
      if (!isAllowedImage(f)) {
        setFile(null);
        setPreview(null);
        setFieldErrors((prev) => ({
          ...prev,
          file: "Only JPG, PNG, or WEBP image files can be uploaded.",
        }));
        e.target.value = "";
        return;
      }

      setFile(f);
      setPreview(URL.createObjectURL(f));
      setFieldErrors((prev) => ({
        ...prev,
        file: "",
      }));
    }
  };

  const validate = () => {
    const errors = {};
    if (!challengeId || !selectedChallenge) {
      errors.challengeId = "Please select the quest or event this submission is for.";
    }
    if (!title.trim())       errors.title       = "Title is required.";
    if (!description.trim()) errors.description = "Description is required.";
    if (!file)               errors.file        = "Please upload an image for your submission.";
    if (file && !isAllowedImage(file)) {
      errors.file = "Only JPG, PNG, or WEBP image files can be uploaded.";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("pfp", file);
      formData.append("title", title.trim());
      formData.append("description", description.trim());
      formData.append("member_bio", member_bio.trim());
      formData.append("challengeId", challengeId);
      formData.append("challengeTitle", selectedChallenge.title || "");
      formData.append("challengeCategory", selectedChallenge.category || "");

      const res = await axios.post("/api/quest/create", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      if (res.data.success) {
        if (res.data.checkoutUrl) {
          window.location.href = res.data.checkoutUrl;
          return;
        }
        setTitle(""); setDescription(""); setMember_bio("");
        setChallengeId(""); setFile(null); setPreview(null); setFieldErrors({});
        onSuccess?.();
      } else {
        const msg = res.data.message || "Submission failed. Please try again.";
        onError?.(msg);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Submission failed. Please try again.";
      if (err?.response?.data?.code || /upload|image|file/i.test(msg)) {
        setFieldErrors((prev) => ({ ...prev, file: msg }));
      }
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl p-8 max-w-4xl mx-auto shadow-lg border border-gray-100">
      <h2 className="font-cinzel text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2">
        Quest Submission
      </h2>
      <p className="font-outfit text-gray-500 text-sm text-center mb-8">
        Share your creative work and connect it to the current quest theme.
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Image Upload */}
        <div>
          <label className="block font-outfit text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
            Submission Image <span className="text-red-500">*</span>
          </label>
          <label
            htmlFor="questImage"
            className={`cursor-pointer rounded-2xl border-2 border-dashed px-6 py-10 flex flex-col items-center justify-center text-center hover:bg-gray-50 transition-colors
              ${fieldErrors.file ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50/40"}`}
          >
            <input type="file" id="questImage" className="hidden" accept="image/jpeg,image/png,image/webp" onChange={handleFileChange} />
            {preview ? (
              <img src={preview} alt="Preview" className="h-48 w-auto object-contain rounded-xl shadow-sm mb-3" />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 shadow-sm flex items-center justify-center mb-3">
                <Upload size={20} className="text-gray-400" strokeWidth={1.5} />
              </div>
            )}
            <p className="font-outfit text-sm text-gray-500">
              {preview ? "Click to change image" : "Click to upload your submission image"}
            </p>
          </label>
          <div className="mt-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <p className="font-outfit text-xs text-gray-400">
              Accepted formats: JPG, PNG, WEBP. File size is shown before submit.
            </p>
            {file && (
              <p className="font-outfit text-xs font-medium text-gray-500">
                Selected: {file.name} ({formatFileSize(file.size)})
              </p>
            )}
          </div>
          {fieldErrors.file && <p className="font-outfit text-xs text-red-500 mt-1">{fieldErrors.file}</p>}
        </div>

        {/* Member ID (read-only) */}
        <div>
          <label className="block font-outfit text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
            Quest / Event <span className="text-red-500">*</span>
          </label>
          <select
            value={challengeId}
            onChange={(e) => {
              setChallengeId(e.target.value);
              setFieldErrors((prev) => ({ ...prev, challengeId: "" }));
            }}
            className={`w-full rounded-full border px-4 py-3 text-sm font-outfit text-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 transition ${
              fieldErrors.challengeId ? "border-red-300 bg-red-50" : "border-gray-200"
            }`}
          >
            <option value="">Select quest or event</option>
            {challenges.map((challenge) => (
              <option key={challenge._id} value={challenge._id}>
                {challenge.title}
                {challenge.category === "events" && challenge.entryFeeCents > 0
                  ? ` - $${(challenge.entryFeeCents / 100).toFixed(2)} entry`
                  : ""}
              </option>
            ))}
          </select>
          {fieldErrors.challengeId && <p className="font-outfit text-xs text-red-500 mt-1">{fieldErrors.challengeId}</p>}
          {selectedFee > 0 && (
            <p className="mt-2 font-outfit text-xs text-amber-600">
              This event requires a ${(selectedFee / 100).toFixed(2)} entry payment. You will be redirected to checkout after the submission is saved.
            </p>
          )}
        </div>

        {/* Member ID (read-only) */}
        <div>
          <label className="block font-outfit text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
            Member ID
          </label>
          <input
            value={userData?.memberId || "—"}
            disabled
            className="w-full rounded-full border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-outfit text-gray-400 cursor-not-allowed"
          />
        </div>

        {/* Title */}
        <div>
          <label className="block font-outfit text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
            Title of Work <span className="text-red-500">*</span>
          </label>
          <input
            value={title}
            onChange={(e) => { setTitle(e.target.value); setFieldErrors((p) => ({ ...p, title: "" })); }}
            type="text"
            className={`w-full rounded-full border px-4 py-3 text-sm font-outfit text-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 transition
              ${fieldErrors.title ? "border-red-300 bg-red-50" : "border-gray-200"}`}
            placeholder="Enter the title of your piece"
          />
          {fieldErrors.title && <p className="font-outfit text-xs text-red-500 mt-1">{fieldErrors.title}</p>}
        </div>

        {/* Description */}
        <div>
          <label className="block font-outfit text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
            Describe Theme Fit <span className="text-red-500">*</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => { setDescription(e.target.value); setFieldErrors((p) => ({ ...p, description: "" })); }}
            rows={5}
            className={`w-full rounded-2xl border px-4 py-3 text-sm font-outfit text-gray-700 resize-none focus:outline-none focus:ring-2 focus:ring-cyan-300 transition
              ${fieldErrors.description ? "border-red-300 bg-red-50" : "border-gray-200"}`}
            placeholder="Share how your work connects to the theme..."
          />
          {fieldErrors.description && <p className="font-outfit text-xs text-red-500 mt-1">{fieldErrors.description}</p>}
        </div>

        {/* Member Bio */}
        <div>
          <label className="block font-outfit text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
            Member Bio <span className="text-gray-400 font-normal normal-case">(optional)</span>
          </label>
          <input
            value={member_bio}
            onChange={(e) => setMember_bio(e.target.value)}
            type="text"
            className="w-full rounded-full border border-gray-200 px-4 py-3 text-sm font-outfit text-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 transition"
            placeholder="A short bio about yourself..."
          />
        </div>

        {/* Submit */}
        <div className="flex justify-center pt-2">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 px-10 py-3 rounded-full bg-gradient-to-r from-[#B6FCFF] to-[#00C3FF] text-black font-semibold font-outfit text-sm shadow-md hover:scale-105 transition-transform disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {loading ? (
              <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Submitting...</>
            ) : (
              <><CheckCircle size={16} strokeWidth={2.5} /> {selectedFee > 0 ? "Save & Pay Entry Fee" : "Submit Entry"}</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default QuestForm;
